import React, { createContext, useContext, useState, useEffect } from "react";

const RatesContext = createContext();

export const useRates = () => {
  const context = useContext(RatesContext);
  if (!context) {
    throw new Error("useRates must be used within a RatesProvider");
  }
  return context;
};

export const RatesProvider = ({ children }) => {
  // State for all gold rates
  const [goldRates, setGoldRates] = useState({
    "24K": 0,
    "22K": 0,
    "18K": 0,
    "14K": 0,
  });

  // State for all silver rates
  const [silverRates, setSilverRates] = useState({
    999: 0,
    925: 0,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Function to calculate gold rates for different purities based on base rate
  const calculateGoldRates = (baseGoldRate) => {
    const baseRate = parseFloat(baseGoldRate);

    return {
      "24K": Math.round(baseRate * (24 / 22)), // 24K = 22K * (24/22)
      "22K": Math.round(baseRate), // Base rate (22K)
      "18K": Math.round(baseRate * (18 / 22)), // 18K = 22K * (18/22)
      "14K": Math.round(baseRate * (14 / 22)), // 14K = 22K * (14/22)
    };
  };

  // Function to calculate silver rates for different purities based on base rate
  const calculateSilverRates = (baseSilverRate) => {
    const baseRate = parseFloat(baseSilverRate);

    return {
      999: Math.round(baseRate), // Base rate (999)
      925: Math.round(baseRate * (925 / 999)), // 925 = 999 * (925/999)
    };
  };

  // Function to fetch rates from API
  const fetchRates = async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch rates directly from the API
      const response = await fetch(
        "https://api.prod.akilajewellers.com/rates/current"
      );
      console.log(response);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const apiData = await response.json();
      console.log(apiData);
      if (apiData.status !== 200 || !apiData.data) {
        throw new Error("Invalid API response format");
      }

      const { gold_rate, silver_rate, show_silver } = apiData.data;

      // Debug logging
      console.log("API Response Data:", apiData.data);
      console.log("Gold Rate:", gold_rate);
      console.log("Silver Rate:", silver_rate);
      console.log("Show Silver Flag:", show_silver);

      // Calculate rates for different purities
      const calculatedGoldRates = calculateGoldRates(gold_rate);

      // Calculate silver rates - use show_silver flag if available, otherwise default to true
      // This ensures silver rates are displayed even if the API doesn't provide the flag
      const shouldShowSilver =
        show_silver !== false && silver_rate && silver_rate > 0;
      const calculatedSilverRates = shouldShowSilver
        ? calculateSilverRates(silver_rate)
        : { 999: 0, 925: 0 };

      console.log("Calculated Silver Rates:", calculatedSilverRates);

      // Update state with calculated rates
      setGoldRates(calculatedGoldRates);
      setSilverRates(calculatedSilverRates);
      setLastUpdated(apiData.data.updated_at || new Date().toISOString());
    } catch (err) {
      console.error("Error fetching rates:", err);
      setError(err.message);

      // Fallback to default rates if API fails
      setGoldRates({
        "24K": 10135,
        "22K": 9290,
        "18K": 7675,
        "14K": 5975,
      });
      setSilverRates({
        999: 126,
        925: 118,
      });

      console.log("Using fallback rates due to API error");
    } finally {
      setLoading(false);
    }
  };

  // Function to get specific gold rate by purity
  const getGoldRate = (purity = "24K") => {
    return goldRates[purity] || goldRates["24K"];
  };

  // Function to get specific silver rate by purity
  const getSilverRate = (purity = "999") => {
    return silverRates[purity] || silverRates["999"];
  };

  // Function to get all gold rates
  const getAllGoldRates = () => {
    return goldRates;
  };

  // Function to get all silver rates
  const getAllSilverRates = () => {
    return silverRates;
  };

  // Function to calculate ornament prices based on current rates
  const calculateOrnamentPrice = (weight, purity, metalType) => {
    const weightInGrams = parseFloat(weight);

    if (metalType === "gold") {
      switch (purity) {
        case "24K":
        case "999":
          return Math.round(weightInGrams * goldRates["24K"]);
        case "22K":
        case "916":
          return Math.round(weightInGrams * goldRates["22K"]);
        case "18K":
        case "750":
          return Math.round(weightInGrams * goldRates["18K"]);
        case "14K":
        case "585":
          return Math.round(weightInGrams * goldRates["14K"]);
        default:
          return Math.round(weightInGrams * goldRates["24K"]);
      }
    } else if (metalType === "silver") {
      switch (purity) {
        case "999":
          return Math.round(weightInGrams * silverRates["999"]);
        case "925":
          return Math.round(weightInGrams * silverRates["925"]);
        default:
          return Math.round(weightInGrams * silverRates["999"]);
      }
    }

    return 0;
  };

  // Function to format rate for display
  const formatRate = (rate, currency = "INR") => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(rate);
  };

  // Function to get rate change (for future implementation)
  const getRateChange = (purity, metalType = "gold") => {
    // This would calculate rate change from previous day
    // For now, return 0
    return 0;
  };

  // Fetch rates on component mount and set up interval
  useEffect(() => {
    fetchRates();

    // Refresh rates every 30 minutes
    const interval = setInterval(fetchRates, 30 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  const value = {
    // Individual rates for backward compatibility
    goldRate: goldRates["22K"],
    silverRate: silverRates["999"],

    // Comprehensive rates
    goldRates,
    silverRates,

    // Utility functions
    getGoldRate,
    getSilverRate,
    getAllGoldRates,
    getAllSilverRates,
    calculateOrnamentPrice,
    formatRate,
    getRateChange,

    // State
    loading,
    error,
    lastUpdated,

    // Actions
    fetchRates,
  };

  return (
    <RatesContext.Provider value={value}>{children}</RatesContext.Provider>
  );
};
